import { ArrowButton } from './ArrowButton'
import { PaginationItem } from './PaginationItem'

interface PaginationProps {
  totalCountOfRegisters: number
  registersPerPage?: number
  currentPage?: number
  onPageChange: (page: number) => void
}

const SIBLINGS_COUNT = 1

function generatePagesArray(from: number, to: number) {
  return Array.from(
    { length: to - from },
    (_, index) => from + index + 1
  ).filter((page) => page > 0)
}

export function Pagination({
  totalCountOfRegisters,
  registersPerPage = 24,
  currentPage = 1,
  onPageChange,
}: Readonly<PaginationProps>) {
  const lastPage =
    Math.ceil(totalCountOfRegisters / registersPerPage) > 50
      ? 50
      : Math.ceil(totalCountOfRegisters / registersPerPage)

  const previousPages =
    currentPage > 1
      ? generatePagesArray(currentPage - 1 - SIBLINGS_COUNT, currentPage - 1)
      : []

  const nextPages =
    currentPage < lastPage
      ? generatePagesArray(
          currentPage,
          Math.min(currentPage + SIBLINGS_COUNT, lastPage)
        )
      : []

  function prevPage() {
    onPageChange(currentPage - 1)
  }

  function nextPage() {
    onPageChange(currentPage + 1)
  }

  return (
    <div className="w-full flex items-center justify-center mt-6">
      <div className="flex items-center gap-[6px]">
        <ArrowButton
          direction="left"
          onClick={() => prevPage()}
          disabled={currentPage <= 1}
        />

        {currentPage > 1 + SIBLINGS_COUNT && (
          <>
            <PaginationItem onPageChange={onPageChange} number={1} />
            {currentPage > 2 + SIBLINGS_COUNT && (
              <span className="w-[40px] h-[40px] flex items-center justify-center text-body-sm text-neutral17">
                ...
              </span>
            )}
          </>
        )}

        {previousPages.length > 0 &&
          previousPages.map((page) => (
            <PaginationItem
              onPageChange={onPageChange}
              key={page}
              number={page}
            />
          ))}

        <PaginationItem
          onPageChange={onPageChange}
          number={currentPage}
          isCurrent
        />

        {nextPages.length > 0 &&
          nextPages.map((page) => (
            <PaginationItem
              onPageChange={onPageChange}
              key={page}
              number={page}
            />
          ))}

        {currentPage + SIBLINGS_COUNT < lastPage && (
          <>
            {currentPage + 1 + SIBLINGS_COUNT < lastPage && (
              <span className="w-[40px] h-[40px] flex items-center justify-center text-body-sm text-neutral17">
                ...
              </span>
            )}
            <PaginationItem onPageChange={onPageChange} number={lastPage} />
          </>
        )}

        <ArrowButton
          direction="right"
          onClick={() => nextPage()}
          disabled={currentPage >= lastPage}
        />
      </div>
    </div>
  )
}
